import type { ReactElement } from "react";
import {
  IconAlertTriangle,
  IconChartLine,
  IconClipboardSearch,
  IconFileDescription,
  IconHistory,
  IconLogout,
  IconMapPin,
  IconPhone,
  IconRefreshAlert,
  IconTool,
} from "@tabler/icons-react";
import type {
  AssignmentCategoryNode,
  FormType,
  VisitCategory,
} from "@/api/form-builder-assignments/formBuilderAssignments.types";

export interface CategoryMeta {
  value: VisitCategory;
  label: string;
  pageLabel: string;
  icon: ReactElement;
  iconBg: string;
  iconColor: string;
}

export const VISIT_CATEGORIES: CategoryMeta[] = [
  {
    value: "reactive",
    label: "Reactive",
    pageLabel: "Reactive Work Orders",
    icon: <IconAlertTriangle size={20} />,
    iconBg: "var(--bg-danger-inverse-default, #ffe0da)",
    iconColor: "var(--icon-danger-default, #c62b30)",
  },
  {
    value: "pm",
    label: "Preventative Maintenance",
    pageLabel: "Preventative Maintenance Work Orders",
    icon: <IconHistory size={20} />,
    iconBg: "var(--bg-accent-cyan-inverse-default, #caecf8)",
    iconColor: "var(--icon-accent-cyan-default, #0e7490)",
  },
  {
    value: "project",
    label: "Projects",
    pageLabel: "Project Work Orders",
    icon: <IconChartLine size={20} />,
    iconBg: "var(--bg-accent-indigo-inverse-default, #dee6ff)",
    iconColor: "var(--icon-accent-indigo-default, #3f51b5)",
  },
  {
    value: "dispatch_call",
    label: "Dispatch Call Script",
    pageLabel: "Dispatch Call Scripts",
    icon: <IconPhone size={20} />,
    iconBg: "var(--bg-info-inverse-default, #d8e8ff)",
    iconColor: "var(--icon-info-default, #1c5bd8)",
  },
];

// Fixed slot list per work-order-type category — non-changeable. Every category
// detail page shows exactly these 10 form_type rows, regardless of what's assigned.
export interface FormTypeSlot {
  formType: FormType;
  label: string;
}

export const CATEGORY_FORM_TYPE_SLOTS: FormTypeSlot[] = [
  { formType: "check_in", label: "Check-In" },
  { formType: "assessment", label: "Assessment" },
  { formType: "further_assessment", label: "Further Assessment" },
  { formType: "different_trade_assessment", label: "Different Trade Assessment" },
  { formType: "another_opinion", label: "Another Opinion" },
  { formType: "repair", label: "Repair" },
  { formType: "different_trade_repair", label: "Different Trade Repair" },
  { formType: "assumed_recall", label: "Assumed Recall" },
  { formType: "recall", label: "Recall" },
  { formType: "check_out", label: "Check-Out" },
];

export function getCategoryMeta(category: string | undefined): CategoryMeta {
  return VISIT_CATEGORIES.find((meta) => meta.value === category) ?? VISIT_CATEGORIES[0];
}

export function normalizeCategories(
  categories: AssignmentCategoryNode[] | undefined,
): AssignmentCategoryNode[] {
  return VISIT_CATEGORIES.map(
    (meta) =>
      categories?.find((node) => node.visitCategory === meta.value) ?? {
        visitCategory: meta.value,
        templateCount: 0,
        assignments: [],
      },
  );
}

export const isMismatched = (entry: { meetsRequirements?: boolean } | null | undefined) =>
  entry?.meetsRequirements === false;

export const countMismatched = (entries: Array<{ meetsRequirements?: boolean }>) =>
  entries.filter(isMismatched).length;

export function countMismatchedAssignments(node: AssignmentCategoryNode | undefined): number {
  if (!node) return 0;
  return countMismatched([...node.assignments, ...(node.inheritedDefaults ?? [])]);
}

export function stripTemplateSuffix(templateName: string | null | undefined): string {
  if (!templateName) return "Default Template";
  const stripped = templateName.replace(/\s*templates?\s*$/i, "").trim();
  return stripped || templateName;
}

export interface VisitIconMeta {
  icon: ReactElement;
  iconBg: string;
  iconColor: string;
}

const VISIT_ICON_RULES: Array<{ pattern: RegExp; meta: VisitIconMeta }> = [
  {
    pattern: /check[\s-]?in/i,
    meta: {
      icon: <IconMapPin size={18} />,
      iconBg: "var(--bg-success-inverse-default, #d7f2e3)",
      iconColor: "var(--icon-success-default, #00683f)",
    },
  },
  {
    pattern: /check[\s-]?out/i,
    meta: {
      icon: <IconLogout size={18} />,
      iconBg: "var(--bg-success-inverse-default, #d7f2e3)",
      iconColor: "var(--icon-success-default, #00683f)",
    },
  },
  {
    pattern: /assessment|opinion/i,
    meta: {
      icon: <IconClipboardSearch size={18} />,
      iconBg: "var(--bg-accent-iris-inverse-default, #ece4ff)",
      iconColor: "var(--icon-accent-iris-default, #6247c9)",
    },
  },
  {
    pattern: /repair/i,
    meta: {
      icon: <IconTool size={18} />,
      iconBg: "var(--bg-warning-inverse-default, #ffedcc)",
      iconColor: "var(--icon-warning-default, #a16207)",
    },
  },
  {
    pattern: /recall/i,
    meta: {
      icon: <IconRefreshAlert size={18} />,
      iconBg: "var(--bg-danger-inverse-default, #ffe0da)",
      iconColor: "var(--icon-danger-default, #c62b30)",
    },
  },
];

export function getVisitIconMeta(label: string): VisitIconMeta {
  const match = VISIT_ICON_RULES.find((rule) => rule.pattern.test(label));
  return (
    match?.meta ?? {
      icon: <IconFileDescription size={18} />,
      iconBg: "var(--bg-neutral-strong, #cfd4dd)",
      iconColor: "var(--icon-default, #363d4d)",
    }
  );
}
